// common
import React from "react"
import { useNavigate } from "react-router-dom"

// components
import Button from "../button"

// API
import API from "../../api"

// styles
import { connect } from "react-redux"
import { mapDispatchProps, mapStateToProps } from "../../store/store"
import { isEmpty } from "../../helpers/object"
import { X } from "react-feather"
import clsx from "clsx"
import Label from "../label"
import { ROUTES } from "../../routes"
import { useLanguage } from "@languages/index"
import { debounce } from "helpers/debounce"

// Props Interface
interface Props {
  className?: any
  card?: any

  style?: any
  width?: number
  color?: string
  marginHorizontal?: number
  marginVertical?: number

  onDeletePress?: any
  onButtonPress?: any
  onCardUpdated?: any

  showModal?: any
  setUser?: any
  setBrand?: any

  auth?: any
}

const Card: React.FunctionComponent<Props> = (props) => {
  if (isEmpty(props.auth)) {
    return <></>
  }

  const language = useLanguage()
  const languageTexts = { ...language.common, ...language.component.card }

  const navigate = useNavigate()

  const navigateWithScroll = (routePath, topOffset = 0) => {
    const scrollToOptions = { top: topOffset, left: 0, behavior: "instant" }
    window.scrollTo(scrollToOptions as unknown as ScrollToOptions) // Temporary solved Typescript bug (https://github.com/microsoft/TypeScript/issues/47441)
    navigate(routePath)
  }

  const fetchUser = async (callback?: any) => {
    try {
      const response = await API.Brand.show({ brandToken: props.auth.token })
      const brand = response.data.brand
      props.setBrand(brand)

      if (callback) {
        callback()
      }

      return brand
    } catch (error) {
      props.showModal({ text: error.message })
    }
  }

  const showToggleActiveModal = (item: any) => {
    props.showModal({
      text: languageTexts.modal_toggle_active_text,
      confirm: () => {
        setTimeout(() => {
          toggleActive(item)
        }, 400)
      },
      showCancelButton: true,
    })
  }

  const toggleActive = (item: any) => {
    API.Card.update({
      id: item.id,
      isActive: item.isActive === true ? false : true,
      brandToken: props.auth.token,
    })
      .then((response) => {
        props.showModal({ text: response.message })
      })
      .catch((error) => {
        props.showModal({ text: error.message })
      })
      .finally(() => {
        if (props.onCardUpdated) {
          props.onCardUpdated()
        }
      })
  }

  const showDeleteCardModal = async (item: any, props) => {
    let buttons = []
    const brand = await fetchUser()

    debounce(() => {
      if (brand.isSeedingPassAutoPurchaseEnabled)
        buttons.push(
          <Button
            className={"min-w-[10rem] py-3 flex justify-center text-center text-base"}
            textColor={"WHITE"}
            buttonColor={"BLACK"}
            text={languageTexts.delete_card_text_1}
            onClick={() => {
              props.closeModal()
              props.closeOverlays()
              navigateWithScroll(ROUTES.CAMPAIGN_PAYMENTS_INDEX.PATH)
            }}
          />
        )
      if (brand.productsInRentableCount > 0)
        buttons.push(
          <Button
            className={"min-w-[10rem] py-3 flex justify-center text-center text-base"}
            textColor={"WHITE"}
            buttonColor={"BLACK"}
            text={languageTexts.delete_card_text_2}
            onClick={() => {
              props.closeModal()
              props.closeOverlays()
              navigateWithScroll(ROUTES.RENTAL_PAYMENTS_INDEX.PATH)
            }}
          />
        )
      if (buttons.length > 0) {
        props.showModal({
          hideCloseButton: true,
          content: () => {
            return (
              <div className="flex flex-col justify-center align-center p-8 bg-white">
                <div className="content flex flex-col">
                  <span className="font-bold text-[22px] text-black">{languageTexts.modal_alert_1_text}</span>
                </div>
                <div className="buttons flex flex-col rows-2 mt-3 gap-1.5">
                  {...buttons}
                  <Button
                    className={"min-w-[10rem] py-3 flex justify-center text-center text-base"}
                    borderColor={"BLACK"}
                    text={languageTexts.cancel}
                    onClick={() => {
                      props.closeModal()
                      props.closeOverlays()
                    }}
                  />
                </div>
              </div>
            )
          },
        })
      } else {
        props.showModal({
          text: languageTexts.modal_delete_confirm,
          confirm: () => {
            setTimeout(() => {
              deleteCard(item)
            }, 400)
          },
          showCancelButton: true,
        })
      }
    }, 400)
  }

  const deleteCard = async (item: any) => {
    await fetchUser()

    try {
      const deleteCardResponse: any = await API.Card.delete({
        id: item.id,
        brandToken: props.auth.token,
      })

      await fetchUser()

      if (props.onCardUpdated) {
        props.onCardUpdated()
      }

      if (deleteCardResponse.message && deleteCardResponse.message.length)
        props.showModal({ text: deleteCardResponse.message })
    } catch (error) {
      if (error.message) props.showModal({ text: error.message })
    }
  }

  return (
    <div
      className={clsx(
        `${props.className ?? ""}`,
        "flex flex-row py-5 px-6 border border-solid border-[#E8E8E8] justify-between"
      )}
    >
      <div className="flex flex-col gap-3 justify-between">
        <div className="flex flex-row gap-2 items-center">
          <div className="">
            <span className="text-[15px] font-bold">{props.card.name}</span>
          </div>
          <div className="">
            {props.card.isActive ? (
              <Label
                text={languageTexts.pay_card}
                className="py-1 px-2 rounded-[4px]"
                textClassName="text-xs font-bold"
              />
            ) : null}
          </div>
        </div>
        <div className="flex flex-row">
          <span>{props.card.cardNumberMasked}</span>
        </div>
      </div>
      <div className="flex flex-col justify-center">
        <div
          className="cursor-pointer"
          onClick={() => {
            if (props.onDeletePress) {
              props.onDeletePress()
            } else {
              showDeleteCardModal(props.card, props)
            }
          }}
        >
          <X size={18} />
        </div>
      </div>
      {/* <Divider marginHorizontal={-20} marginVertical={0} /> */}
      {/* <div style={styles.activeButtonContainer}>
        <Button
          onClick={() => {
            if (props.onButtonPress) {
              props.onButtonPress()
            } else {
              showToggleActiveModal(props.card)
            }
          }}
          text={props.card.isActive ? '결제카드' : '결제카드로 설정하기'}
          justify={'center'}
          textStyle={{
            ...styles.activeButtonText,
            ...(props.card.isActive ? { fontWeight: '800' } : {}),
          }}
          borderColor={color}
          icon={
            props.card.isActive
              ? {
                  position: 'centerRight',
                  icon: <Check style={{ marginLeft: 4 }} />,
                }
              : null
          }
        />
      </div> */}
    </div>
  )
}

export default connect(mapStateToProps, mapDispatchProps)(Card)
